import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

type SaveFilterViewPanelProps = {
  readonly editingName: string | null;
  readonly isBusy: boolean;
  readonly isCapReached: boolean;
  readonly onSave: (name: string) => Promise<boolean>;
  readonly onUpdate: () => void;
  readonly onCapErrorDismiss: () => void;
};

/**
 * The footer of a filter dropdown that offers saved filters: a name box plus Save, or —
 * while a saved filter is being edited — its name plus Update.
 *
 * @example
 * <SaveFilterViewPanel
 *   editingName={views.editingView?.name ?? null}
 *   isBusy={views.isBusy}
 *   isCapReached={views.isSaveCapReached}
 *   onSave={views.saveView}
 *   onUpdate={views.updateEditedView}
 *   onCapErrorDismiss={views.dismissSaveCapError}
 * />
 */
export function SaveFilterViewPanel({
  editingName,
  isBusy,
  isCapReached,
  onSave,
  onUpdate,
  onCapErrorDismiss,
}: SaveFilterViewPanelProps) {
  const { t } = useTranslation('general');
  const capErrorId = useId();
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = editingName !== null;
  const [wasEditing, setWasEditing] = useState(isEditing);

  // Dropped on every mode flip, or a name typed but never saved reappears in the input
  // after the user visits edit mode and comes back.
  if (wasEditing !== isEditing) {
    setWasEditing(isEditing);
    setName('');
  }

  if (editingName !== null) {
    return (
      <div className="px-4 pt-4 pb-2 border-t border-neut-border flex items-center gap-2">
        <span className="flex-1 min-w-0 text-sm text-neut-sec truncate">
          {t('save_filter_panel.editing_label', { name: editingName })}
        </span>
        <button
          type="button"
          disabled={isBusy}
          aria-disabled={isBusy}
          onClick={onUpdate}
          className="flex-shrink-0 px-2 py-2 text-sm font-medium bg-prim text-neut-0 rounded-md hover:bg-prim-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('save_filter_panel.update_button')}
        </button>
      </div>
    );
  }

  const trimmed = name.trim();
  const isSaveDisabled = !trimmed || isBusy || isSubmitting;

  return (
    <div className="px-4 pt-3 pb-2 border-t border-neut-border flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={name}
          maxLength={80}
          disabled={isSubmitting}
          aria-label={t('save_filter_panel.aria_name_input')}
          aria-describedby={isCapReached ? capErrorId : undefined}
          placeholder={t('save_filter_panel.name_placeholder')}
          onChange={(event) => {
            setName(event.target.value);
            if (isCapReached) onCapErrorDismiss();
          }}
          className="flex-1 min-w-0 py-2 px-3 text-sm border border-neut-border rounded-md bg-neut-0 text-neut-prim placeholder:text-neut-400 focus:outline-none focus:border-prim"
        />
        <button
          type="button"
          disabled={isSaveDisabled}
          aria-disabled={isSaveDisabled}
          onClick={() => {
            setIsSubmitting(true);
            void onSave(trimmed)
              .then((isSaved) => {
                if (isSaved) setName('');
              })
              .finally(() => setIsSubmitting(false));
          }}
          className="flex-shrink-0 px-3 py-2 text-sm font-medium bg-prim text-neut-0 rounded-md hover:bg-prim-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('save_filter_panel.save_button')}
        </button>
      </div>
      {isCapReached && (
        <span id={capErrorId} className="text-xs text-error-600">
          {t('save_filter_panel.error_cap')}
        </span>
      )}
    </div>
  );
}
